/* eslint-disable @atlassian/relay/unused-fields, @atlassian/relay/must-colocate-fragment-spreads */
import { useCallback } from 'react';
import {
	commitLocalUpdate,
	graphql,
	useRelayEnvironment,
	fetchQuery,
	type Environment,
} from 'react-relay';
import type {
	issueFetchAndAppendQuery,
	issueFetchAndAppendQuery$data,
} from '@atlassian/jira-relay/src/__generated__/issueFetchAndAppendQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import { useShouldSkipIssuesConnectionUpdate } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/use-should-skip-issues-connection-update/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useIsDensityFull,
	useIsInlineEditingEnabled,
} from '../../controllers/features/selectors.tsx';
import {
	issueConnectionUpdater,
	type IssueInsertionArgs,
	removeHighlightAfterDelay,
} from '../../controllers/issue-connection-updater/index.tsx';
import { useIssueFetchQueryVariables } from '../../controllers/issue-fetch-query-variables/index.tsx';

const issueConnectionQueryUpdater = (
	environment: Environment,
	response: issueFetchAndAppendQuery$data,
	issueConnectionId: string,
	insertionArgs?: IssueInsertionArgs,
	groupId?: string | null,
) => {
	const issue = response?.jira?.issueByKey;
	if (!issue) {
		return;
	}

	commitLocalUpdate(environment, (store) => {
		issueConnectionUpdater(store, {
			...insertionArgs,
			issue,
			issueConnectionId,
			issueRecordId: issue.__id,
			fieldSetsConnectionId: issue.fieldSetsById.__id,
			groupId,
		});
	});
	removeHighlightAfterDelay(environment, issue);
};

/**
 * Fetch required data for an issue to be displayed within the issue table and insert the record to the provided issue
 * connection.
 *
 * @param issueKey Key of the issue to fetch.
 * @param insertionArgs Arguments to determine where the fetched issue should be inserted in the connection.
 */
type OnIssueFetchAndAppend = (issueKey: string, insertionArgs?: IssueInsertionArgs) => void;

/**
 * Returns a callback that will fetch required data for an issue (by issue key) to be displayed within the issue table
 * and append the record to the provided issue connection.
 *
 * @param issueConnectionId ID of the Relay connection where the new issue should be appended.
 */
export const useIssueFetchAndAppend = ({
	issueConnectionId,
	groupContextFieldInput,
	jql,
}: {
	issueConnectionId: string | null;
	groupContextFieldInput?: GroupContextField;
	jql?: string | null;
}): OnIssueFetchAndAppend => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const isDensityFull = useIsDensityFull();
	const isInlineEditingEnabled = useIsInlineEditingEnabled();
	const { fieldSetIds } = useIssueFetchQueryVariables();
	const shouldSkipIssuesConnectionUpdateAndShowFlag = useShouldSkipIssuesConnectionUpdate();

	return useCallback(
		(issueKey: string, insertionArgs?: IssueInsertionArgs) => {
			if (!issueConnectionId) {
				return null;
			}
			fetchQuery<issueFetchAndAppendQuery>(
				environment,
				graphql`
					query issueFetchAndAppendQuery(
						$cloudId: ID!
						$issueKey: String!
						$fieldSetIds: [String!]!
						$isInlineEditingEnabled: Boolean!
						$isDensityFull: Boolean!
						$shouldQueryFieldsByIdOrAlias: Boolean!
						$fieldIdsOrAliases: [String!]!
						$issueSearchInput: JiraIssueSearchInput!
						$shouldQueryMatchesIssueSearch: Boolean!
						$isGroupByAnyFieldsTechnicalEnabled: Boolean!
					) {
						jira {
							issueByKey(cloudId: $cloudId, key: $issueKey) {
								...highlightIssueRow_update_isHighlightedIssueRow
								__id
								key
								issueId
								fieldSetsById(fieldSetIds: $fieldSetIds, first: 500) @required(action: THROW) {
									__id
									...issueRow_nativeIssueTable_IssueRowWithFragments_fieldSets
										@arguments(
											isInlineEditingEnabled: $isInlineEditingEnabled
											isDensityFull: $isDensityFull
										)
								}
								fieldsByIdOrAlias(idsOrAliases: $fieldIdsOrAliases)
									@optIn(to: "JiraIssueFieldsByIdOrAlias")
									@include(if: $shouldQueryFieldsByIdOrAlias) {
									__typename
									fieldId
									... on JiraStatusField {
										status {
											statusId
											name
										}
									}
									... on JiraPriorityField {
										priority {
											priorityId
										}
									}
									... on JiraSingleSelectUserPickerField {
										user {
											accountId
										}
									}
									... on JiraSingleSelectField {
										fieldOption {
											optionId
										}
									}
									... on JiraSprintField {
										selectedSprintsConnection(first: 1) {
											edges {
												node {
													sprintId
												}
											}
										}
									}
									... on JiraNumberField {
										number
									}
									... on JiraLabelsField @include(if: $isGroupByAnyFieldsTechnicalEnabled) {
										# NOTE: selecting all labels as the relevant label is not necessarily first
										selectedLabelsConnection {
											edges {
												node {
													name
												}
											}
										}
									}
								}
								matchesIssueSearch(issueSearchInput: $issueSearchInput)
									@include(if: $shouldQueryMatchesIssueSearch)
							}
						}
					}
				`,
				{
					cloudId,
					issueKey,
					fieldSetIds,
					isInlineEditingEnabled,
					isDensityFull,
					...(groupContextFieldInput
						? {
								shouldQueryFieldsByIdOrAlias: true,
								fieldIdsOrAliases: [groupContextFieldInput.fieldId],
							}
						: {
								shouldQueryFieldsByIdOrAlias: false,
								fieldIdsOrAliases: [],
							}),
					...(jql
						? {
								shouldQueryMatchesIssueSearch: true,
								issueSearchInput: { jql },
							}
						: {
								shouldQueryMatchesIssueSearch: false,
								issueSearchInput: { jql: '' },
							}),
					isGroupByAnyFieldsTechnicalEnabled: fg('group_by_any_fields_technical'),
				},
			).subscribe({
				next: (data) => {
					if (
						shouldSkipIssuesConnectionUpdateAndShowFlag(
							groupContextFieldInput,
							data?.jira?.issueByKey,
						)
					) {
						return;
					}

					issueConnectionQueryUpdater(
						environment,
						data,
						issueConnectionId,
						insertionArgs,
						groupContextFieldInput?.groupId,
					);
				},
			});
		},
		[
			cloudId,
			environment,
			fieldSetIds,
			isDensityFull,
			isInlineEditingEnabled,
			issueConnectionId,
			groupContextFieldInput,
			jql,
			shouldSkipIssuesConnectionUpdateAndShowFlag,
		],
	);
};
